import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { BsHouse, BsBuilding, BsHouses, BsPinMapFill } from 'react-icons/bs';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Button from 'react-bootstrap/Button';
import BayPagination from '@bay-ui/BayPagination';
import BayViews from '@bay-ui/BayViews';
import { Tooltip } from '@bay-email/editor';
import { useTruncateCheck } from '@bay-ui/utilities/hook';
import ListingsDataGrid from '@bay-email/extensions/components/ListingsDataGrid';
import TitleCell from '@bay-email/extensions/components/ListingsDataGrid/TitleCell';
import { DataType, SortDirection, SortingMode } from 'ka-table/enums';
import ListingFilterDrawer from '../SingleListing/ListingFilterDrawer';

interface MultipleListingsDrawerProps {
	children?: React.ReactNode;
	disabled?: boolean;
	listings: any[];
	selectedListingIds?: string[];
	total: number;
	size: number;
	loading: boolean;
	onPaginationChange: (params: {
		page: number;
		size: number;
		keyword?: string;
		filters?: ListingFiltersPayload;
		sortBy?: string;
		orderDirection?: 'ASC' | 'DESC';
	}) => void;
	onConfirm: (selectedIds: string[]) => void;
	/**
	 * The drawer became visible. Panels use this to defer loading the search grid
	 * until it is actually going to be looked at.
	 */
	onOpen?: () => void;
	t: (key: string) => string;
	setActiveView?: (activeView: { id: 'mylistings' | 'companylistings'; page: number; size: number }) => void;
	/** Bump to open the drawer programmatically — see the edge-guard effect below. */
	openSignal?: number;
}

interface ListingFiltersPayload {
	startdate?: string;
	enddate?: string;
	propertytype?: string[];
	pricemin?: string;
	pricemax?: string;
	status?: string[];
}

interface ListingSortPayload {
	sortBy?: string;
	orderDirection?: 'ASC' | 'DESC';
}

interface SelectOption {
	label: string;
	value: string;
}

const ALL_PROPERTY_TYPE_VALUES = ['residential', 'multifamily', 'commercial', 'land'];

const pageSizes = [10, 25, 50, 100];

// Helper function to format date
const formatDateListed = (dateString?: string) => {
	if (!dateString || dateString === '-' || dateString === 'Invalid Date') return '-';
	try {
		let date: Date;
		// Handle already formatted date like "09/06/2024 at 4:09:06 PM"
		if (dateString.includes(' at ')) {
			const datePart = dateString.split(' at ')[0];
			const [month, day, year] = datePart.split('/');
			date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
		}
		// Handle YYYYMMDDHHMMSS format (e.g., 20240906160906)
		else if (/^\d{14}$/.test(dateString)) {
			const year = parseInt(dateString.substring(0, 4));
			const month = parseInt(dateString.substring(4, 6)) - 1; // Month is 0-indexed
			const day = parseInt(dateString.substring(6, 8));
			date = new Date(year, month, day);
		} else {
			date = new Date(dateString);
		}
		if (isNaN(date.getTime())) return '-';
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric'
		});
	} catch (e) {
		return '-';
	}
};

const getInitialViews = () => [
	{
		'id': 'mylistings',
		'viewname': 'My Listings',
		'active': true,
		'paginationData': {
			page: 1,
			size: 10,
		}
	},
	{
		'id': 'companylistings',
		'viewname': 'Company Listings',
		'active': false,
		'paginationData': {
			page: 1,
			size: 10,
		}
	}
];

const ListingAgentCell: React.FC<{ name?: string }> = ({ name }) => {
	const displayName = name || '-';
	const [nameRef, isNameTruncated] = useTruncateCheck([displayName]) as [React.RefObject<HTMLDivElement>, boolean];

	return (
		<Tooltip disabled={!isNameTruncated} placement='top' content={displayName}>
			<div ref={nameRef} className='text-capitalize text-truncate'>
				{displayName}
			</div>
		</Tooltip>
	);
};

export const MultipleListingsDrawer: React.FC<MultipleListingsDrawerProps> = (props) => {
	const { t } = props;
	const [visible, setVisible] = useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(props.size || 10);
	const [searchValue, setSearchValue] = useState<string>('');
	const [priceMin, setPriceMin] = useState<string>('');
	const [priceMax, setPriceMax] = useState<string>('');
	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');
	const [selectedStatus, setSelectedStatus] = useState<SelectOption[]>([]);
	const [selectedPropertyType, setSelectedPropertyType] = useState<SelectOption[]>([]);
	const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(false);
	const [sortConfig, setSortConfig] = useState<ListingSortPayload>({});
	const [selectedRows, setSelectedRows] = useState<string[]>([]);
	const [applyAttempted, setApplyAttempted] = useState<boolean>(false);
	const [viewData, setViewData] = useState(getInitialViews());
	const onPaginationChangeRef = useRef(props.onPaginationChange);
	const onOpenRef = useRef(props.onOpen);
	const searchValueRef = useRef(searchValue);
	const pageSizeRef = useRef(pageSize);
	const sortConfigRef = useRef(sortConfig);

	const statusOptions = [
		{ label: 'Active', value: 'active' },
		{ label: 'Sold', value: 'sold' },
		{ label: 'Withdrawn', value: 'withdrawn' },
		{ label: 'Expired', value: 'expired' },
		{ label: 'Rented', value: 'rented' },
		{ label: 'Leased', value: 'leased' },
		{ label: 'Off Market', value: 'off market' },
		{ label: 'Canceled', value: 'canceled' },
		{ label: 'Coming Soon', value: 'coming soon' },
		{ label: 'Under Contract', value: 'under contract' },
	];
	const propertyTypeOptions = [
		{ label: 'Residential', value: 'residential' },
		{ label: 'Land', value: 'land' },
		{ label: 'Multi-family', value: 'multifamily' },
		{ label: 'Commercial', value: 'commercial' },
	];

	const tFunction = useCallback((key: string) => t(key), [t]);

	// Keep refs updated with latest values
	useEffect(() => {
		onPaginationChangeRef.current = props.onPaginationChange;
	}, [props.onPaginationChange]);

	useEffect(() => {
		onOpenRef.current = props.onOpen;
	}, [props.onOpen]);

	useEffect(() => {
		searchValueRef.current = searchValue;
	}, [searchValue]);

	useEffect(() => {
		pageSizeRef.current = pageSize;
	}, [pageSize]);

	useEffect(() => {
		sortConfigRef.current = sortConfig;
	}, [sortConfig]);

	const getPropertyTypeIcon = (type?: string) => {
		switch (type) {
			case 'land':
				return <BsPinMapFill className='me-2' size={16} />;
			case 'multifamily':
				return <BsHouses className='me-2' size={16} />;
			case 'commercial':
				return <BsBuilding className='me-2' size={16} />;
			default:
				return <BsHouse className='me-2' size={16} />;
		}
	};

	const tableProps = useMemo(() => {
		const isPriceSorted = sortConfig.sortBy === 'list_price';
		const isDateListedSorted = sortConfig.sortBy === 'adddatetime';
		const getSortDirection = (isSorted: boolean) => {
			if (!isSorted) return SortDirection.Descend;
			return sortConfig.orderDirection === 'ASC' ? SortDirection.Ascend : SortDirection.Descend;
		};

		return {
			sortingMode: SortingMode.SingleRemote,
			columns: [
				{
					key: 'address',
					width: 276,
					title: 'ADDRESS & MLS #',
					dataType: DataType.String,
					customStyles: {
						paddingLeft: 0,
						paddingRight: 0,
						width: 276,
					},
					customComponent: ({ rowData }: any) => {
						return (
							<TitleCell
								data={rowData}
							/>
						);
					}
				},
				{
					key: 'status',
					width: 140,
					title: 'STATUS',
					dataType: DataType.String,
					customComponent: ({ rowData }: any) => {
						return (
							<div className='text-capitalize'>{rowData?.status || '-'}</div>
						);
					},
				},
				{
					key: 'price',
					width: 140,
					title: 'PRICE',
					dataType: DataType.String,
					sortBy: 'list_price',
					sortDirection: getSortDirection(isPriceSorted),
					customComponent: ({ rowData }: any) => {
						return (
							<div>${new Intl.NumberFormat('en-US').format(rowData?.list_price || 0)}</div>
						);
					},
				},
				{
					key: 'propertyType',
					width: 140,
					title: 'PROPERTY TYPE',
					dataType: DataType.String,
					customComponent: ({ rowData }: any) => {
						return (
							<div className='text-capitalize d-flex align-items-center'>
								{getPropertyTypeIcon(rowData?.propertyType?.toLowerCase())}
								<span>{rowData?.propertyType || '-'}</span>
							</div>
						);
					},
				},
				{
					key: 'listingAgent',
					width: 140,
					title: 'LISTING AGENT',
					dataType: DataType.String,
					customComponent: ({ rowData }: any) => {
						return (
							<ListingAgentCell name={rowData?.agentName} />
						);
					},
				},
				{
					key: 'dateListed',
					width: 140,
					title: 'DATE LISTED',
					dataType: DataType.String,
					sortBy: 'adddatetime',
					sortDirection: getSortDirection(isDateListedSorted),
					customComponent: ({ rowData }: any) => {
						return <div>{formatDateListed(rowData?.dateListed)}</div>;
					},
				},
			],
			rowKeyField: 'id'
		};
	}, [sortConfig]);

	useEffect(() => {
		setApplyAttempted(false);
		if (!visible) {
			// Reset when drawer closes
			setSelectedRows([]);
			setSearchValue('');
			setPriceMin('');
			setPriceMax('');
			setStartDate('');
			setEndDate('');
			setSelectedStatus([]);
			setSelectedPropertyType([]);
			setIsFilterExpanded(false);
			return;
		}
		// Initialize selected rows when drawer opens
		if (props.selectedListingIds && Array.isArray(props.selectedListingIds) && props.selectedListingIds.length > 0) {
			setSelectedRows([...props.selectedListingIds]);
		} else {
			setSelectedRows([]);
		}
		onOpenRef.current?.();
		// Only fetch data on initial open, not on every visible change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible]);

	const filterCount = useMemo(() => {
		let count = 0;
		if (priceMin?.trim() || priceMax?.trim()) count += 1;
		if (startDate?.trim() || endDate?.trim()) count += 1;
		if (selectedStatus?.length) count += 1;
		if (selectedPropertyType?.length) count += 1;
		return count;
	}, [priceMin, priceMax, startDate, endDate, selectedStatus, selectedPropertyType]);

	const getActiveFilters = useCallback((): ListingFiltersPayload => {
		const filters: ListingFiltersPayload = {};
		if (startDate?.trim()) filters.startdate = startDate.trim();
		if (endDate?.trim()) filters.enddate = endDate.trim();
		if (priceMin?.trim()) filters.pricemin = priceMin.trim();
		if (priceMax?.trim()) filters.pricemax = priceMax.trim();
		if (selectedStatus?.length) filters.status = selectedStatus.map(option => option.value);
		filters.propertytype = selectedPropertyType?.length
			? selectedPropertyType.map(option => option.value)
			: ALL_PROPERTY_TYPE_VALUES;
		return filters;
	}, [endDate, priceMax, priceMin, selectedPropertyType, selectedStatus, startDate]);

	const handleViewChange = useCallback((views: any[]) => {
		const activeView = views?.find(v => v?.active);
		setViewData(views);

		if (!activeView) return;

		const { id, paginationData = {} } = activeView;
		const {page = 1, size = 10 } = paginationData;

		props.setActiveView?.({ id, page, size });
		setPageSize(size);
		setCurrentPage(page);
		setSearchValue('');
		setPriceMin('');
		setPriceMax('');
		setStartDate('');
		setEndDate('');
		sortConfigRef.current = {};
		setSortConfig({});
		setSelectedStatus([]);
		setSelectedPropertyType([]);
		setIsFilterExpanded(false);
	}, [props.setActiveView]);

	const handleViewPaginationChange = useCallback((newPageSize: number, targetPage: number) => {
		setViewData((prev) => {
			return prev.map(view => ({
				...view,
				paginationData: view.active ? { page: targetPage, size: newPageSize } : view.paginationData
			}));
		});
	  }, []);

	const handlePageChange = useCallback((page: number, size: number) => {
		// BayPagination calls this when page or size changes - always update
		const isPageSizeChange = size !== pageSizeRef.current;

		// Always update state first
		setPageSize(size);

		// Determine which page to use
		const targetPage = isPageSizeChange ? 1 : page;

		// Always update current page
		setCurrentPage(targetPage);

		// Always call onPaginationChange with latest values
		onPaginationChangeRef.current({
			page: targetPage,
			size,
			keyword: searchValueRef.current,
			filters: getActiveFilters(),
			sortBy: sortConfigRef.current.sortBy,
			orderDirection: sortConfigRef.current.orderDirection,
		});
		handleViewPaginationChange(size, targetPage);
	}, [getActiveFilters]);

	const handleSearch = useCallback((keyword: string) => {
		setSearchValue(keyword);
		setCurrentPage(1);
		props.onPaginationChange({
			page: 1,
			size: pageSize,
			keyword,
			filters: getActiveFilters(),
			sortBy: sortConfigRef.current.sortBy,
			orderDirection: sortConfigRef.current.orderDirection,
		});
		handleViewPaginationChange(pageSize, 1);
	}, [getActiveFilters, pageSize, props]);

	const handleSortChange = useCallback((nextSort: { sortBy: string; orderDirection: 'ASC' | 'DESC' }) => {
		setSortConfig(nextSort);
		setCurrentPage(1);
		onPaginationChangeRef.current({
			page: 1,
			size: pageSizeRef.current,
			keyword: searchValueRef.current,
			filters: getActiveFilters(),
			sortBy: nextSort.sortBy,
			orderDirection: nextSort.orderDirection,
		});
		handleViewPaginationChange(pageSizeRef.current, 1);
	}, [getActiveFilters, handleViewPaginationChange]);

	const handleSelectionChange = useCallback((selected: string[]) => {
		setSelectedRows(selected || []);
		if (selected?.length >= 2) setApplyAttempted(false);
	}, []);

	// openSignal is an EDGE, not a level — see SingleListingDrawer.tsx for the
	// full rationale. Adopting the current value on mount without acting makes
	// only a genuine bump open the drawer, so a remount with the signal still
	// sitting at its last value doesn't reopen it on its own.
	const handledOpenSignalRef = useRef<number | null>(null);
	useEffect(() => {
		const signal = typeof props.openSignal === 'number' ? props.openSignal : 0;
		if (handledOpenSignalRef.current === null) {
			handledOpenSignalRef.current = signal;
			return;
		}
		if (props.disabled || signal < 1 || signal === handledOpenSignalRef.current) {
			return;
		}
		handledOpenSignalRef.current = signal;
		setVisible(true);
	}, [props.disabled, props.openSignal]);

	const handleCancel = useCallback(() => {
		setVisible(false);
	}, []);

	const handleConfirm = useCallback(() => {
		if (!selectedRows || selectedRows.length < 2) {
			setApplyAttempted(true);
			return;
		}
		props.onConfirm(selectedRows);
		setVisible(false);
	}, [props, selectedRows]);

	// Shown only by a failed Apply click; hidden once 2+ listings are selected.
	// Dropping below 2 again does not re-show it until the next Apply click.
	const shouldShowMinSelectAlert = applyAttempted;

	return (
		<>
			<Offcanvas
				show={visible}
				onHide={handleCancel}
				placement="end"
				style={{ width: '1080px' }}
				backdrop={true}
				scroll={false}
				className="multiple-listings-drawer"
			>
				<Offcanvas.Header closeButton className="border-bottom px-5 py-4">
					<Offcanvas.Title>
						<h1 className="m-0">{t('Select Properties')}</h1>
					</Offcanvas.Title>
				</Offcanvas.Header>
				<Offcanvas.Body className="d-flex flex-column h-100 p-0">
					<div className="d-flex flex-column gap-4 flex-fill px-5 pb-4" style={{ minHeight: 0 }}>
						<div id="listings-view" >
							<BayViews
								customModalContent={true}
								isAllowRestoreDefault={false}
								t={t}
								viewData={viewData}
								showDivider={false}
								setActiveView={handleViewChange}
							/>
						</div>

						<ListingFilterDrawer
							loading={props.loading}
							filterCount={filterCount}
							defaultExpanded={isFilterExpanded}
							searchValue={searchValue}
							onSearch={handleSearch}
							onSearchValueChange={setSearchValue}
							onFilterButtonClick={setIsFilterExpanded}
							priceMin={priceMin}
							priceMax={priceMax}
							onPriceMinChange={setPriceMin}
							onPriceMaxChange={setPriceMax}
							startDate={startDate}
							endDate={endDate}
							onStartDateChange={setStartDate}
							onEndDateChange={setEndDate}
							statusOptions={statusOptions}
							selectedStatus={selectedStatus}
							onStatusChange={setSelectedStatus}
							propertyTypeOptions={propertyTypeOptions}
							selectedPropertyType={selectedPropertyType}
							onPropertyTypeChange={setSelectedPropertyType}
							onClose={() => {}}
							onClearAll={() => {
								setPriceMin('');
								setPriceMax('');
								setStartDate('');
								setEndDate('');
								setSelectedStatus([]);
								setSelectedPropertyType([]);
								setIsFilterExpanded(false);
								setCurrentPage(1);
								onPaginationChangeRef.current({
									page: 1,
									size: pageSizeRef.current,
									keyword: searchValueRef.current,
									filters: {
										startdate: '',
										enddate: '',
										propertytype: ALL_PROPERTY_TYPE_VALUES,
										pricemin: '',
										pricemax: '',
										status: [],
									},
									sortBy: sortConfigRef.current.sortBy,
									orderDirection: sortConfigRef.current.orderDirection,
								});
								handleViewPaginationChange(pageSizeRef.current, 1);
							}}
							onApplyFilter={() => {
								setIsFilterExpanded(false);
								setCurrentPage(1);
								onPaginationChangeRef.current({
									page: 1,
									size: pageSizeRef.current,
									keyword: searchValueRef.current,
									filters: getActiveFilters(),
									sortBy: sortConfigRef.current.sortBy,
									orderDirection: sortConfigRef.current.orderDirection,
								});
								handleViewPaginationChange(pageSizeRef.current, 1);
							}}
						/>

						{shouldShowMinSelectAlert && (
							<div className="alert alert-danger m-0 text-red border-0 fw-bold" role="alert">
								{t('Please select at least 2 listings to proceed.')}
							</div>
						)}

						<div className="flex-fill overflow-auto" style={{ minHeight: 0 }}>
							<ListingsDataGrid
								data={props.listings || []}
								enableRowSelection={true}
								selectionMode="multiple"
								initTableProps={tableProps}
								loading={props.loading}
								setSelectedData={handleSelectionChange}
								onSortChange={handleSortChange}
								t={tFunction}
								selectedListing={props.selectedListingIds}
								hasSearch={!!searchValue?.trim()}
								hasFilters={filterCount > 0}
							/>
						</div>

						{props.listings && props.listings.length > 0 && (
							<div className="bg-white d-flex align-items-center justify-content-center">
								<BAYPaginationShim
									pageSizeOptions={pageSizes}
									selectedPageSize={pageSize}
									totalRowCount={props.total}
									totalPages={Math.ceil(props.total / pageSize)}
									activePage={currentPage}
									handlePageChange={handlePageChange}
									disabled={props.loading}
									t={tFunction}
									pageSizeSelectorLabel={t('Rows per page')}
									pageSizeMenuPlacement="top"
									pageSizeMenuZIndex={1060}
									isPageSizeDropDown={true}
								/>
							</div>
						)}
					</div>

					<div className="p-3 d-flex justify-content-between gap-2 border-top">
						<Button variant="outline-secondary" onClick={handleCancel}>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleConfirm}>
							Apply
						</Button>
					</div>
				</Offcanvas.Body>
			</Offcanvas>
			<div onClick={() => !props.disabled && setVisible(true)} style={{ cursor: props.disabled ? 'not-allowed' : 'pointer' }}>{props.children}</div>
		</>
	);
};

// Shim to reuse BayPagination without re-export issues in this context
const BAYPaginationShim = (props: any) => {
	return <BayPagination {...props} />;
};

export default MultipleListingsDrawer;
